import { useState } from "react";
import { decryptSecret } from "./crypto";
import { FetchError } from "./requests/FetchError";
import { RequestState } from "./requests/RequestState";

interface RetrieveSecretProps {
  secretId: string;
  encryptionKey: string | null;
}

export function RetrieveSecret({
  secretId,
  encryptionKey,
}: RetrieveSecretProps) {
  const [retrieveState, setRetrieveState] = useState<RequestState<string>>({
    status: "idle",
  });
  const [retrievedSecret, setRetrievedSecret] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle",
  );

  const missingKey = !encryptionKey;

  async function fetchSecret(id: string, key: string | null) {
    if (!key) {
      const error: FetchError = {
        code: "MISSING_KEY",
        message:
          "This link must include the secret key fragment to decrypt the secret. Use the full link from the sender.",
      };
      setRetrieveState({ status: "error", error });
      return;
    }

    try {
      setRetrieveState({ status: "loading" });

      const response = await fetch(`/secrets/${encodeURIComponent(id)}`, {
        method: "POST",
      });
      const data = await response.json();
      if (!response.ok) {
        const error: FetchError = {
          code: "FETCH_FAILED",
          message: data.error || "Secret not found or already retrieved",
        };
        throw error;
      }

      const plaintext = await decryptSecret(data.encryptedSecret, key);
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
      setRetrievedSecret(plaintext);
      setRetrieveState({ status: "success", data: plaintext });
    } catch (error) {
      let fetchError: FetchError;
      if (error instanceof Error && "code" in error) {
        fetchError = error as FetchError;
      } else if (error instanceof Error) {
        fetchError = { code: "UNKNOWN_ERROR", message: error.message };
      } else {
        fetchError = {
          code: "UNKNOWN_ERROR",
          message: "Failed to retrieve secret. Please try again.",
        };
      }
      setRetrieveState({ status: "error", error: fetchError });
    }
  }

  async function copySecret(secret: string) {
    try {
      await navigator.clipboard.writeText(secret);
      setCopyStatus("copied");
      setTimeout(() => setCopyStatus("idle"), 2000);
    } catch {
      setCopyStatus("failed");
      setTimeout(() => setCopyStatus("idle"), 2000);
    }
  }

  return (
    <div>
      <h1>Retrieve Secret</h1>
      <div className="banner banner-warning">
        ⚠️ This secret can only be viewed <strong>once</strong>. Copy it before
        leaving this page.
      </div>

      {retrieveState.status === "idle" ? (
        <div>
          <p className="muted" style={{ marginTop: "1rem" }}>
            Retrieval starts only after you click the button below.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              void fetchSecret(secretId, encryptionKey);
            }}
            disabled={missingKey}
          >
            Reveal Secret
          </button>
          {missingKey ? (
            <p className="muted" style={{ marginTop: "0.6rem" }}>
              This link is missing the key fragment. Use the full link from the
              sender.
            </p>
          ) : null}
        </div>
      ) : null}

      {retrieveState.status === "loading" ? (
        <p className="muted">Retrieving…</p>
      ) : retrieveState.status === "success" && retrievedSecret ? (
        <div>
          <label>Secret</label>
          <div className="secret-box">{retrievedSecret}</div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              void copySecret(retrievedSecret);
            }}
          >
            {copyStatus === "copied"
              ? "Copied!"
              : copyStatus === "failed"
                ? "Copy failed"
                : "Copy"}
          </button>
          <p className="muted" style={{ marginTop: "1rem" }}>
            This secret has been permanently deleted from the server.
          </p>
          <p className="muted" style={{ marginTop: "0.4rem" }}>
            <a href="/">Create a new secret</a>
          </p>
        </div>
      ) : retrieveState.status === "error" ? (
        <div>
          <div className="banner banner-info">
            {retrieveState.error.message}
          </div>
          <p className="muted">
            <a href="/">Create a new secret</a>
          </p>
        </div>
      ) : null}
    </div>
  );
}
